import React, { useCallback, useMemo, useId } from 'react';
import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import FieldLabel from './FieldLabel';
import { ILabel, ILabelOption } from '../constants/interfaces';
import {
  getBaseFormControlStyles,
  getAccessibilityProps,
  getFieldName,
  getHelperText,
} from '../styles/fieldStyles';

/**
 * Props for the DropdownField component
 */
export interface IDropdownFieldProps {
  /** Current selected value */
  value: string;
  /** Callback fired when the selection changes */
  onChange: (value: string) => void;
  /** Field configuration containing label text, options, validation rules, etc. */
  field: ILabel;
  /** Error message to display */
  errorText?: string;
  /** Whether to hide the label */
  isLabelHidden?: boolean;
  /** Width of the dropdown */
  width?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Callback fired when dropdown gains focus */
  onFocus?: () => void;
  /** Callback fired when dropdown loses focus */
  onBlur?: () => void;
}

/**
 * Utility functions for dropdown handling
 */
const dropdownUtils = {
  /**
   * Gets the submitted/stored value for an option.
   */
  getOptionValue: (option: ILabelOption): string => option.value ?? option.text,

  /**
   * Finds option by value/text
   */
  findOptionByValue: (
    options: ILabelOption[],
    value: string
  ): ILabelOption | undefined => {
    return options.find(
      (option) => dropdownUtils.getOptionValue(option) === value
    );
  },

  /**
   * Gets the display text for a selected value
   */
  getDisplayText: (
    options: ILabelOption[],
    value: string,
    placeholder: string
  ): React.ReactNode => {
    if (!value) {
      return (
        <Typography
          component="span"
          sx={{
            color: 'rgba(51, 51, 64, 0.72)',
            fontStyle: 'italic',
          }}
          variant="body2"
        >
          {placeholder}
        </Typography>
      );
    }

    const option = dropdownUtils.findOptionByValue(options, value);
    return option ? option.text : value;
  },

  /**
   * Validates that the selected value exists in options
   */
  isValidOption: (options: ILabelOption[], value: string): boolean => {
    return (
      !value ||
      options.some((option) => dropdownUtils.getOptionValue(option) === value)
    );
  },

  /**
   * Validates dropdown selection based on field requirements
   */
  validateSelection: (value: string, field: ILabel): string | null => {
    if (field.isMandatory && !value.trim()) {
      return `${field.text} is required`;
    }
    return null;
  },
};

/**
 * DropdownField Component
 *
 * A reusable dropdown/select component that integrates with form validation
 * and provides a consistent interface for option selection across the application.
 *
 * Features:
 * - Dynamic option loading from field configuration
 * - Comprehensive validation and error handling
 * - Accessibility support with labels and ARIA attributes
 * - Consistent styling with the design system
 * - Support for empty/placeholder state
 * - Memoized performance optimizations
 *
 * @example
 * ```tsx
 * <DropdownField
 *   value={selectedCountry}
 *   onChange={handleCountryChange}
 *   field={countryField}
 *   errorText={validationError}
 *   placeholder="Select a country"
 * />
 * ```
 */
function DropdownField(props: IDropdownFieldProps) {
  const {
    value,
    onChange,
    field,
    errorText,
    isLabelHidden = false,
    width,
    disabled = false,
    readOnly = false,
    placeholder,
    onFocus,
    onBlur,
  } = props;

  // Generate unique ID for accessibility
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  // Memoize options and computed values
  const computedValues = useMemo(() => {
    // Sort options by orderIndex, then alphabetically by text
    const options = (field.options || []).slice().sort((a, b) => {
      const aOrder = Number(a.orderIndex);
      const bOrder = Number(b.orderIndex);
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      const aText = (a.text || '').toString().toLowerCase();
      const bText = (b.text || '').toString().toLowerCase();
      return aText.localeCompare(bText);
    });
    const hasError = Boolean(errorText);
    const helperText = getHelperText(errorText);
    const placeholderText = placeholder || 'Please select';
    const isValidValue = dropdownUtils.isValidOption(options, value);

    return {
      hasError,
      helperText,
      isDisabled: disabled || readOnly,
      isValidValue,
      options,
      placeholderText,
    };
  }, [field.options, errorText, placeholder, value, disabled, readOnly]);

  // Optimized change handler with useCallback
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = event.target.value;
      onChange(newValue);
    },
    [onChange]
  );

  // Optimized focus handler
  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  // Optimized blur handler
  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  // Memoize render value function
  const renderValue = useCallback(
    (selected: unknown) => {
      return dropdownUtils.getDisplayText(
        computedValues.options,
        selected as string,
        computedValues.placeholderText
      );
    },
    [computedValues.options, computedValues.placeholderText]
  );

  const isFullWidth = width === '100%';

  return (
    <>
      {/* Label with proper accessibility attributes */}
      <FieldLabel field={field} htmlFor={fieldId} isHidden={isLabelHidden} />

      {/* Dropdown with enhanced props */}
      <FormControl
        fullWidth={isFullWidth}
        sx={getBaseFormControlStyles(width)}
        size="small"
        error={computedValues.hasError}
      >
        <Select
          fullWidth={isFullWidth}
          labelId={labelId}
          id={fieldId}
          name={getFieldName(field)}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={computedValues.isDisabled}
          sx={{ textAlign: 'left' }}
          displayEmpty
          renderValue={renderValue}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 200,
                overflow: 'auto',
              },
            },
          }}
          inputProps={getAccessibilityProps(
            fieldId,
            labelId,
            errorId,
            field,
            isLabelHidden,
            computedValues.hasError
          )}
        >
          {/* Empty/placeholder option */}
          <MenuItem
            value=""
            sx={{
              color: '#888',
              fontSize: '14px',
              fontStyle: 'italic',
            }}
          >
            <em>{computedValues.placeholderText}</em>
          </MenuItem>

          {/* Dynamic options from field configuration */}
          {computedValues.options.map((option) => (
            <MenuItem
              key={option.labelOptionId}
              value={dropdownUtils.getOptionValue(option)}
              sx={{ fontSize: '14px' }}
            >
              {option.text}
            </MenuItem>
          ))}
        </Select>

        {/* Helper text with proper accessibility */}
        <FormHelperText id={computedValues.hasError ? errorId : undefined}>
          {computedValues.helperText}
        </FormHelperText>
      </FormControl>
    </>
  );
}

export default DropdownField;

// Export utilities for testing and external use
export { dropdownUtils };
